import { useEffect } from 'react'
import { signal } from '@preact/signals-react'

/**
 * Counts every change of a tab's dirty flag.
 *
 * `tab.store.isDirty` is a plain property, and the tab header reads it while it
 * draws. The edit screens set it in an effect — after the header has drawn — so
 * the "*" came one change late, and in WebKit not at all while typing. Whatever
 * shows the flag reads this signal too, and draws again when it moves.
 */
export const dirtyRevision = signal(0)

/** Sets a tab's dirty flag and tells whoever shows it. */
export const setTabDirty = (tab: any, dirty: boolean) => {
    if (!tab?.store) return
    const next = Boolean(dirty)
    if (Boolean(tab.store.isDirty) === next) return
    tab.store.isDirty = next
    dirtyRevision.value++
}

export const useTabDirty = (tab: any, dirty: boolean) => {
    useEffect(() => {
        if (!tab) return
        setTabDirty(tab, dirty)
        return () => {
            // don’t leak dirty state if tab object is reused
            setTabDirty(tab, false)
        }
    }, [tab, dirty])
}

export const useTabSaveHandler = (tab: any, save: (() => Promise<void> | void) | null) => {
    useEffect(() => {
        if (!tab) return
        tab.store.save = save
        return () => {
            tab.store.save = null
        }
    }, [tab, save])
}
